import React, { FC } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { OnDemandValidationFormData } from "./validation.schema";
import { Program } from "@/domain/programs.interface";
import Modal from "@/components/Modal";
import theme from "@/utils/theme";

export interface OnDemandValidationRequestModalPresenterProps {
  open: boolean;
  isSubmitting: boolean;
  isLoadingPrograms: boolean;
  programs: Program[];
  selectedProgram: Program | null;
  onClose: () => void;
  onSubmit: () => void;
  onProgramChange: (programId: string) => void;
}

export const OnDemandValidationRequestModalPresenter: FC<
  OnDemandValidationRequestModalPresenterProps
> = ({
  open,
  isSubmitting,
  isLoadingPrograms,
  programs,
  selectedProgram,
  onClose,
  onSubmit,
  onProgramChange,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useFormContext<OnDemandValidationFormData>();

  const programId = watch("programId");

  const contentNode = isLoadingPrograms ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 200,
      }}
    >
      <CircularProgress />
    </Box>
  ) : programs.length === 0 ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 200,
      }}
    >
      <Typography variant="bodyMedium" color="text.secondary">
        There are no programs configured in your account
      </Typography>
    </Box>
  ) : (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 2,
      }}
    >
      <Typography variant="titleSmall">
        Enter the day(s) of the program in which an on-demand validation request
        should activate, separated by commas. Values cannot be lower than{" "}
        {selectedProgram?.parameters?.programDays || "..."} or greater than 365.
        (e.g.: 60, 90, 120)
      </Typography>
      {/* Program Dropdown */}
      <Controller
        name="programId"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            label="Program"
            fullWidth
            error={!!errors.programId}
            helperText={errors.programId?.message}
            required
            onChange={(e) => {
              field.onChange(e);
              onProgramChange(e.target.value);
            }}
          >
            {programs.map((program) => (
              <MenuItem key={program._id} value={program._id}>
                {program.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {/* On-demand days Validation Requests */}
      <Controller
        name="onDemandValidationDays"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="On-demand days Validation Requests"
            fullWidth
            error={!!errors.onDemandValidationDays}
            helperText={errors.onDemandValidationDays?.message}
            required
            placeholder="60, 90, 120"
            multiline
            rows={2}
          />
        )}
      />
    </Box>
  );

  const actionsNode = (
    <>
      <Button
        onClick={onClose}
        variant="text"
        disabled={isSubmitting}
        sx={{
          textTransform: "none",
          color: "primary.main",
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        color="primary"
        disabled={isSubmitting || isLoadingPrograms || programs.length === 0}
        sx={{
          textTransform: "none",
          minWidth: 100,
        }}
      >
        {isSubmitting ? <CircularProgress size={24} /> : "Accept"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isSubmitting}
      theme={theme}
      title="On demand validation requests"
      content={contentNode}
      actions={actionsNode}
      size={{
        width: "100%",
        minHeight: 400,
        maxHeight: 600,
        maxWidth: 460,
      }}
    />
  );
};
